import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { IWidgetControllerConfig } from '@wix/native-components-infra/dist/src/types/types';
import type { FulfillmentMethod } from '@wix/ambassador-restaurants-v1-fulfillment-method/types';
import {
  AvailabilityStatus,
  type TimeSlot as AvailabilityTimeSlot,
} from '@wix/ambassador-restaurants-menu-settings-v1-menu-ordering-settings/types';

import { operationsService } from '../../services/operationsService';
import { menuService } from '../../services/menuService';
import { itemService } from '../../services/itemService';
import { ordersSettingsService } from '../../services/ordersSettingsService';
import { fulfillmentsService } from '../../services/fulfillmentsService';
import { getOperationsMock } from '../../editor/editorMocks/getOperationsMock';
import { getMenusMock } from '../../editor/editorMocks/getMenusMock';
import { getItemsMock } from '../../editor/editorMocks/getItemsMock';
import { getMockMenus } from '../../editor/editorMocks/menus';
import {
  getTimeSlotsMock,
  getTimeSlotsPerOperationMock,
} from '../../editor/editorMocks/getTimeSlotsMock';
import { getFulfillmentMethodsMock } from '../../editor/editorMocks/getFulfillmentMethodMocks';
import { noop } from '../../utils/noop';
import { createStorageProvider } from '../../utils/storageContext';
import { RequestStatus, wrapRequest } from '../../utils/wrapRequest';
import {
  getInstance,
  getRegionalSettings,
  getSiteCurrency,
  getSiteTimezone,
} from '../../utils/flowAPI';
import { getTimezoneOffset } from '../../utils/time';
import {
  DispatchType,
  type Address,
  type DispatchesInfo,
  type TimeSlot,
} from '../../types/businessTypes';
import type { Operation } from '../../types/operation';
import type { MenuItem, PopulatedMenu } from '../../types/menu';

import type {
  OpenFulfillmentModalParams,
  OpenFulfillmentModalResult,
} from './actions/openFulfillmentModal';

interface OnlineOrdersData {
  regionalSettings?: string;
  timezone: string;
  isPremium: boolean;
  fitToContentHeight: boolean;
  operations: Operation[];
  currentOperation?: Operation;
  isLocationSelected: boolean;
  menus: PopulatedMenu[];
  itemMap: Record<string, MenuItem>;
  cartQuantities: Record<string, number>;
  fetchOperationsStatus: RequestStatus;
  fetchMenusStatus: RequestStatus;
  fetchItemsStatus: RequestStatus;
  fulfillmentMethods: FulfillmentMethod[];
  fetchFulfillmentMethodsStatus: RequestStatus;
  selectedDispatchType?: DispatchType;
  currency: string;
  timeSlots: TimeSlot[];
  fetchTimeSlotsStatus: RequestStatus;
  timeSlotsByOperationId: Record<string, TimeSlot[]>;
  fetchTimeSlotsByOperationStatus: RequestStatus;
  acceptingDispatchTypesByOperationId: Record<string, DispatchType[]>;
  dispatchesInfo: Partial<DispatchesInfo>;
  hasError: boolean;
}

interface OnlineOrdersActions {
  fetchOperations: (
    operationGroupId?: string,
  ) => Promise<Operation[] | undefined>;
  fetchMenus: (operationId: string) => Promise<PopulatedMenu[] | undefined>;
  getCachedOperationMenus: (operationId: string) => PopulatedMenu[] | undefined;
  applyMenuAvailability: (
    operationId: string,
    timeSlot?: AvailabilityTimeSlot,
  ) => Promise<PopulatedMenu[] | undefined>;
  fetchItems: (
    itemIds: string[],
  ) => Promise<Record<string, MenuItem> | undefined>;
  fetchFulfillmentMethods: (
    fulfillmentIds: string[],
  ) => Promise<FulfillmentMethod[] | undefined>;
  fetchTimeSlots: (
    operation: Operation,
    deliveryAddress?: Address,
  ) => Promise<TimeSlot[] | undefined>;
  fetchTimeSlotsByOperation: (
    operationIds: string[],
    deliveryAddress?: Address,
  ) => Promise<Record<string, TimeSlot[]> | undefined>;
  setIsLocationSelected: (value: boolean) => void;
  setSelectedDispatchType: (type: DispatchType) => void;
  setDispatchesInfo: (dispatchesInfo: Partial<DispatchesInfo>) => void;
  setAcceptingDispatchTypesByOperationId: (
    map: Record<string, DispatchType[]>,
  ) => void;
  onItemClick: (item: MenuItem, menuId: string, sectionId: string) => void;
  openCart: () => void;
  openFulfillmentModal: (
    params: OpenFulfillmentModalParams,
  ) => Promise<OpenFulfillmentModalResult | undefined>;
}

export interface OnlineOrdersStorage
  extends OnlineOrdersData,
    OnlineOrdersActions {}

const defaultOnlineOrdersStorage: OnlineOrdersStorage = {
  timezone: '',
  isPremium: false,
  fitToContentHeight: true,
  operations: [],
  isLocationSelected: true,
  menus: [],
  itemMap: {},
  cartQuantities: {},
  fetchOperationsStatus: RequestStatus.DEFAULT,
  fetchMenusStatus: RequestStatus.DEFAULT,
  fetchItemsStatus: RequestStatus.DEFAULT,
  fulfillmentMethods: [],
  fetchFulfillmentMethodsStatus: RequestStatus.DEFAULT,
  currency: '',
  timeSlots: [],
  fetchTimeSlotsStatus: RequestStatus.DEFAULT,
  timeSlotsByOperationId: {},
  fetchTimeSlotsByOperationStatus: RequestStatus.DEFAULT,
  acceptingDispatchTypesByOperationId: {},
  dispatchesInfo: {},
  hasError: false,
  fetchOperations: noop,
  fetchMenus: noop,
  getCachedOperationMenus: () => undefined,
  applyMenuAvailability: () => Promise.resolve(undefined),
  fetchItems: noop,
  fetchFulfillmentMethods: noop,
  fetchTimeSlots: noop,
  fetchTimeSlotsByOperation: noop,
  setIsLocationSelected: noop,
  setSelectedDispatchType: noop,
  setDispatchesInfo: noop,
  setAcceptingDispatchTypesByOperationId: noop,
  onItemClick: noop,
  openCart: noop,
  openFulfillmentModal: noop,
};

const { withStorageProvider, useStorage } = createStorageProvider(
  defaultOnlineOrdersStorage,
);

const initOnlineOrdersStorage = (
  flowAPI: ControllerFlowAPI,
  controllerConfig: IWidgetControllerConfig,
): OnlineOrdersStorage => {
  const timezone = getSiteTimezone(flowAPI);
  const timezoneOffset = getTimezoneOffset(timezone);
  const currency = getSiteCurrency(flowAPI);
  const { metaSiteId } = getInstance(flowAPI);
  const { setProps } = controllerConfig;

  const fetchOperations = async (
    flowApi: ControllerFlowAPI,
    operationGroupId?: string,
  ) => {
    const operations = operationGroupId
      ? await operationsService.getOperationsByGroupId(
          flowApi.httpClient,
          flowApi.errorHandler.withErrorHandler,
          operationGroupId,
        )
      : await operationsService.listOperations(
          flowApi.httpClient,
          flowApi.errorHandler.withErrorHandler,
        );

    return operations ?? [];
  };

  let operationMenusCache:
    | { operationId: string; menus: PopulatedMenu[] }
    | undefined;

  const fetchMenus = async (
    flowApi: ControllerFlowAPI,
    operationId: string,
  ) => {
    const availability = await menuService.getMenusAvailabilityByOperation(
      flowApi,
      operationId,
      timezoneOffset,
    );
    const menuIds = Object.keys(availability ?? {});
    const populatedMenus = await menuService.getPopulatedMenus(
      flowApi,
      menuIds,
      timezone,
    );

    operationMenusCache = {
      operationId,
      menus: populatedMenus ?? [],
    };

    return operationMenusCache.menus;
  };

  const getCachedOperationMenus = (
    operationId: string,
  ): PopulatedMenu[] | undefined =>
    operationMenusCache?.operationId === operationId
      ? operationMenusCache.menus
      : undefined;

  const applyMenuAvailability = async (
    flowApi: ControllerFlowAPI,
    operationId: string,
    timeSlot?: AvailabilityTimeSlot,
  ): Promise<PopulatedMenu[] | undefined> => {
    if (
      !operationMenusCache ||
      operationMenusCache.operationId !== operationId
    ) {
      setProps({ menus: [] });

      return undefined;
    }

    const operationMenus = operationMenusCache.menus;

    if (!timeSlot) {
      setProps({ menus: operationMenus });

      return operationMenus;
    }

    const response = await ordersSettingsService
      .listMenusAvailabilityByTimeslot(flowApi, operationId, timeSlot)
      .catch((error) => {
        flowAPI.panoramaClient
          .errorMonitor()
          .reportError(new Error('applyMenuAvailability slot filter failed'));

        return null;
      });

    if (response === null) {
      setProps({ menus: operationMenus });

      return operationMenus;
    }

    const availableMenuIds = new Set<string>();

    for (const status of response?.menusAvailabilityStatus ?? []) {
      if (
        status.availabilityStatus === AvailabilityStatus.AVAILABLE &&
        status.menuId
      ) {
        availableMenuIds.add(status.menuId);
      }
    }

    const filtered = operationMenus.filter((menu) =>
      availableMenuIds.has(menu.id),
    );

    setProps({ menus: filtered });

    return filtered;
  };

  const fetchItems = async (flowApi: ControllerFlowAPI, itemIds: string[]) => {
    const items = await itemService.getItems(
      flowApi,
      itemIds,
      currency,
      metaSiteId,
      timezone,
    );
    const itemMap: Record<string, MenuItem> = {};

    items.forEach((item) => {
      if (item.id) {
        itemMap[item.id] = item;
      }
    });

    return itemMap;
  };

  const fetchTimeSlots = (
    flowApi: ControllerFlowAPI,
    operation: Operation,
    deliveryAddress?: Address,
  ): Promise<TimeSlot[]> =>
    fulfillmentsService.fetchFirstAvailableTimeSlot(
      flowApi,
      operation.id,
      deliveryAddress,
    );

  const fetchTimeSlotsByOperation = (
    flowApi: ControllerFlowAPI,
    operationIds: string[],
    deliveryAddress?: Address,
  ): Promise<Record<string, TimeSlot[]>> =>
    fulfillmentsService.fetchFirstAvailableTimeSlotsPerOperation(
      flowApi,
      operationIds,
      deliveryAddress,
    );

  return {
    ...defaultOnlineOrdersStorage,
    currency,
    timezone,
    regionalSettings: getRegionalSettings(flowAPI),
    fetchOperations: wrapRequest(
      flowAPI,
      fetchOperations,
      'operations',
      'fetchOperationsStatus',
    ),
    fetchMenus: wrapRequest(flowAPI, fetchMenus, 'menus', 'fetchMenusStatus'),
    getCachedOperationMenus,
    applyMenuAvailability: (operationId, timeSlot) =>
      applyMenuAvailability(flowAPI, operationId, timeSlot),
    fetchItems: wrapRequest(flowAPI, fetchItems, 'itemMap', 'fetchItemsStatus'),
    fetchFulfillmentMethods: wrapRequest(
      flowAPI,
      (flowApi, fulfillmentIds) =>
        fulfillmentsService.fetchAllFulfillments(flowApi, fulfillmentIds),
      'fulfillmentMethods',
      'fetchFulfillmentMethodsStatus',
    ),
    fetchTimeSlots: wrapRequest(
      flowAPI,
      fetchTimeSlots,
      'timeSlots',
      'fetchTimeSlotsStatus',
    ),
    fetchTimeSlotsByOperation: wrapRequest(
      flowAPI,
      fetchTimeSlotsByOperation,
      'timeSlotsByOperationId',
      'fetchTimeSlotsByOperationStatus',
    ),
    setIsLocationSelected: (value) => setProps({ isLocationSelected: value }),
    setSelectedDispatchType: (type) => setProps({ selectedDispatchType: type }),
    setDispatchesInfo: (dispatchesInfo) => setProps({ dispatchesInfo }),
    setAcceptingDispatchTypesByOperationId: (map) =>
      setProps({ acceptingDispatchTypesByOperationId: map }),
  };
};

const mockOnlineOrdersStorage = (
  flowAPI: ControllerFlowAPI,
  controllerConfig: IWidgetControllerConfig,
): OnlineOrdersStorage => {
  const initiatedStorage = initOnlineOrdersStorage(flowAPI, controllerConfig);

  return {
    ...initiatedStorage,
    cartQuantities: { 'mock-item': 1 },
    fetchOperations: wrapRequest(
      flowAPI,
      getOperationsMock,
      'operations',
      'fetchOperationsStatus',
    ),
    fetchMenus: wrapRequest(flowAPI, getMenusMock, 'menus', 'fetchMenusStatus'),
    getCachedOperationMenus: () => getMockMenus(),
    applyMenuAvailability: (operationId) => getMenusMock(flowAPI, operationId),
    fetchItems: wrapRequest(
      flowAPI,
      getItemsMock,
      'itemMap',
      'fetchItemsStatus',
    ),
    fetchFulfillmentMethods: wrapRequest(
      flowAPI,
      getFulfillmentMethodsMock,
      'fulfillmentMethods',
      'fetchFulfillmentMethodsStatus',
    ),
    fetchTimeSlots: wrapRequest(
      flowAPI,
      getTimeSlotsMock,
      'timeSlots',
      'fetchTimeSlotsStatus',
    ),
    fetchTimeSlotsByOperation: wrapRequest(
      flowAPI,
      getTimeSlotsPerOperationMock,
      'timeSlotsByOperationId',
      'fetchTimeSlotsByOperationStatus',
    ),
  };
};

export {
  initOnlineOrdersStorage,
  mockOnlineOrdersStorage,
  withStorageProvider as withOnlineOrdersStorageProvider,
  useStorage as useOnlineOrdersStorage,
};
